// Critical Path Method (CPM) for Gantt tasks
// - Duration = days between first and last date column values (min 1)
// - Milestone = duration 0 (same first/last date) or task.task/subtask begins with "(Milestone)"
// - Dependencies: task.dependencies field, row numbers (1-based) or task UUIDs
// Returns: Map of taskId -> { es, ef, ls, lf, slack, critical, isMilestone, duration }

import { parseDate } from "@/lib/gantt-utils";

function taskDuration(task, dateCols) {
  const dates = task.dates || {};
  let min = null;
  let max = null;
  for (const col of dateCols) {
    const v = dates[col.id];
    if (!v) continue;
    const d = parseDate(v);
    if (!d) continue;
    if (min == null || d < min) min = d;
    if (max == null || d > max) max = d;
  }
  if (min == null || max == null) return { min: null, max: null, duration: 0 };
  const days = Math.max(0, Math.round((max - min) / 86400000));
  return { min, max, duration: days };
}

function isMilestone(task, duration) {
  const name = (task.task || task.subtask || "").trim();
  if (name.toLowerCase().includes("milestone")) return true;
  return duration === 0;
}

function resolveDeps(task, idxById, idxByNum, taskIdx, taskCount) {
  const raw = (task.dependencies || "")
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const out = new Set();
  for (const d of raw) {
    const asNum = parseInt(d, 10);
    if (String(asNum) === d && idxByNum.has(asNum)) {
      out.add(idxByNum.get(asNum));
    } else if (idxById.has(d)) {
      out.add(idxById.get(d));
    }
  }
  // Structured links also contribute predecessors
  for (const l of task.dependency_links || []) {
    const otherIdx = idxById.has(l.target_id) ? idxById.get(l.target_id) : -1;
    if (otherIdx < 0) continue;
    if (l.kind === "blocks") {
      // this blocks other → other has this as a predecessor (handled when iterating other)
      // for THIS task's predecessor list: nothing
      continue;
    }
    // blocked_by: other → this
    out.add(otherIdx);
  }
  return Array.from(out);
}

// Returns adjacency where preds[i] = list of predecessor indices for task i
function buildPredecessors(tasks, idxById, idxByNum) {
  const preds = tasks.map(() => new Set());
  tasks.forEach((t, i) => {
    for (const p of resolveDeps(t, idxById, idxByNum, i, tasks.length)) {
      if (p !== i) preds[i].add(p);
    }
  });
  // Apply "blocks" links: if A.blocks(B), then B's preds gets A
  tasks.forEach((t, i) => {
    for (const l of t.dependency_links || []) {
      if (l.kind !== "blocks") continue;
      const otherIdx = idxById.has(l.target_id) ? idxById.get(l.target_id) : -1;
      if (otherIdx < 0 || otherIdx === i) continue;
      preds[otherIdx].add(i);
    }
  });
  return preds.map((s) => Array.from(s));
}

export function computeCriticalPath(tasks, dateCols) {
  if (!tasks || tasks.length === 0) {
    return { map: new Map(), criticalIds: new Set(), criticalEdges: new Set(), duration: 0 };
  }
  const idxById = new Map(tasks.map((t, i) => [t.id, i]));
  const idxByNum = new Map(tasks.map((_, i) => [i + 1, i]));

  // Compute duration & milestone flag for each task
  const info = tasks.map((t) => {
    const { duration } = taskDuration(t, dateCols);
    return { duration, isMilestone: isMilestone(t, duration) };
  });

  // Build adjacency: predecessors (resolved indices) per task — covers both legacy and structured deps
  const preds = buildPredecessors(tasks, idxById, idxByNum);

  // Build successors
  const succs = tasks.map(() => []);
  preds.forEach((ps, i) => {
    ps.forEach((p) => {
      if (p !== i) succs[p].push(i);
    });
  });

  // Topological order via Kahn's algorithm (break cycles by keeping original order)
  const inDeg = preds.map((ps) => ps.length);
  const order = [];
  const queue = [];
  for (let i = 0; i < tasks.length; i++) if (inDeg[i] === 0) queue.push(i);
  while (queue.length > 0) {
    const v = queue.shift();
    order.push(v);
    for (const s of succs[v]) {
      inDeg[s] -= 1;
      if (inDeg[s] === 0) queue.push(s);
    }
  }
  // If cyclic, append remaining in original order (best-effort)
  if (order.length < tasks.length) {
    for (let i = 0; i < tasks.length; i++) if (!order.includes(i)) order.push(i);
  }

  // Forward pass: ES, EF
  const es = new Array(tasks.length).fill(0);
  const ef = new Array(tasks.length).fill(0);
  for (const v of order) {
    const parentEnd = preds[v].length > 0 ? Math.max(...preds[v].map((p) => ef[p])) : 0;
    es[v] = parentEnd;
    ef[v] = es[v] + info[v].duration;
  }

  const projectEnd = ef.length > 0 ? Math.max(...ef) : 0;

  // Backward pass: LF, LS
  const lf = new Array(tasks.length).fill(projectEnd);
  const ls = new Array(tasks.length).fill(projectEnd);
  for (let k = order.length - 1; k >= 0; k--) {
    const v = order[k];
    const childStart = succs[v].length > 0 ? Math.min(...succs[v].map((s) => ls[s])) : projectEnd;
    lf[v] = childStart;
    ls[v] = lf[v] - info[v].duration;
  }

  // Slack & critical
  const criticalIds = new Set();
  const map = new Map();
  for (let i = 0; i < tasks.length; i++) {
    const slack = ls[i] - es[i];
    const critical = slack === 0 && info[i].duration >= 0;
    if (critical) criticalIds.add(tasks[i].id);
    map.set(tasks[i].id, {
      es: es[i],
      ef: ef[i],
      ls: ls[i],
      lf: lf[i],
      slack,
      critical,
      isMilestone: info[i].isMilestone,
      duration: info[i].duration,
    });
  }

  // Critical edges: an edge p->v is critical iff p and v are both critical AND ef[p] == es[v]
  const criticalEdges = new Set();
  for (let v = 0; v < tasks.length; v++) {
    if (!criticalIds.has(tasks[v].id)) continue;
    for (const p of preds[v]) {
      if (criticalIds.has(tasks[p].id) && ef[p] === es[v]) {
        criticalEdges.add(`${p}->${v}`);
      }
    }
  }

  return { map, criticalIds, criticalEdges, duration: projectEnd, preds, succs };
}
